import React from 'react';
import { Twitch, ExternalLink, Video, Users, MessageCircle, Settings } from 'lucide-react';

const TwitchPage = () => {
  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-purple-600 rounded-xl">
              <Twitch size={28} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Twitch</h1>
              <p className="text-gray-400">Streaming Dashboard</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <a 
            href="https://dashboard.twitch.tv" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-purple-500 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-600/20 rounded-lg group-hover:bg-purple-600/40 transition-colors">
                <Settings size={20} className="text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold">Creator Dashboard</h3>
                <p className="text-sm text-gray-500">Manage your channel</p>
              </div>
              <ExternalLink size={16} className="ml-auto text-gray-600 group-hover:text-purple-400" />
            </div>
          </a>

          <a 
            href="https://www.twitch.tv/directory/following" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-purple-500 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-600/20 rounded-lg group-hover:bg-purple-600/40 transition-colors">
                <Video size={20} className="text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold">Following</h3>
                <p className="text-sm text-gray-500">Channels you follow</p>
              </div>
              <ExternalLink size={16} className="ml-auto text-gray-600 group-hover:text-purple-400" />
            </div>
          </a>

          <a 
            href="https://www.twitch.tv/popout/moderator" 
            target="_blank" 
            rel="noopener noreferrer"
            className="p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-purple-500 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-600/20 rounded-lg group-hover:bg-purple-600/40 transition-colors">
                <Users size={20} className="text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold">Mod View</h3>
                <p className="text-sm text-gray-500">Moderation tools</p>
              </div>
              <ExternalLink size={16} className="ml-auto text-gray-600 group-hover:text-purple-400" />
            </div>
          </a>

          <a 
            href="https://kallie-creatr.com" 
            className="p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-cyan-500 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-600/20 rounded-lg group-hover:bg-cyan-600/40 transition-colors">
                <MessageCircle size={20} className="text-cyan-400" />
              </div>
              <div>
                <h3 className="font-semibold">OBS Control</h3>
                <p className="text-sm text-gray-500">Open streaming software</p>
              </div>
              <ExternalLink size={16} className="ml-auto text-gray-600 group-hover:text-cyan-400" />
            </div>
          </a>
        </div>

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stream Preview Placeholder */}
          <div className="lg:col-span-2 bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Video size={20} className="text-purple-400" />
              Stream Preview
            </h2>
            <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Twitch size={48} className="mx-auto mb-4 text-purple-500 opacity-50" />
                <p className="text-gray-500">Stream preview will appear here when live</p>
                <a 
                  href="https://dashboard.twitch.tv/stream-manager" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                >
                  Open Stream Manager
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="space-y-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h2 className="text-xl font-bold mb-4">Quick Links</h2>
              <div className="space-y-2">
                <a 
                  href="https://www.twitch.tv/settings/channel" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <Settings size={18} className="text-gray-400" />
                  <span>Channel Settings</span>
                </a>
                <a 
                  href="https://dashboard.twitch.tv/analytics" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <Video size={18} className="text-gray-400" />
                  <span>Analytics</span>
                </a>
                <a 
                  href="https://dashboard.twitch.tv/community/roles" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <Users size={18} className="text-gray-400" />
                  <span>Manage Roles</span>
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border border-purple-700/50 rounded-xl p-6">
              <h3 className="font-bold mb-2">Pro Tip</h3>
              <p className="text-sm text-gray-300">
                Use the OBS link in the sidebar to quickly access your streaming software controls.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwitchPage;
